/**
 * Formats a subscription's recurring billing period into a localized suffix that
 * sits next to the price (e.g. "/ year", "/ month", "every 3 months").
 * Returns null when there's no period to show (physical / one-time carts).
 */

import type { LocalizedTexts } from "@/types/layout";
import type { BillingPeriod } from "@/types/cart";
import { t } from "@/utils/translations";

const SINGLE_INTERVAL_KEYS: Record<string, [string, string]> = {
  day: ["Tracker.Checkout.Period.PerDay", "/ day"],
  week: ["Tracker.Checkout.Period.PerWeek", "/ week"],
  month: ["Tracker.Checkout.Period.PerMonth", "/ month"],
  year: ["Tracker.Checkout.Period.PerYear", "/ year"],
};

const PLURAL_UNIT_KEYS: Record<string, [string, string]> = {
  day: ["Tracker.Checkout.Period.UnitDays", "days"],
  week: ["Tracker.Checkout.Period.UnitWeeks", "weeks"],
  month: ["Tracker.Checkout.Period.UnitMonths", "months"],
  year: ["Tracker.Checkout.Period.UnitYears", "years"],
};

export function formatBillingPeriodLabel(
  period: BillingPeriod | null | undefined,
  localizedTexts: LocalizedTexts
): string | null {
  if (!period?.interval) return null;

  const interval = period.interval.toLowerCase();
  const frequency = period.frequency ?? 1;

  if (frequency <= 1) {
    const single = SINGLE_INTERVAL_KEYS[interval];
    return single ? t(localizedTexts, single[0], single[1]) : null;
  }

  const unit = PLURAL_UNIT_KEYS[interval];
  if (!unit) return null;
  const unitLabel = t(localizedTexts, unit[0], unit[1]);
  // Fall back to the English pattern if a translation drops a placeholder,
  // otherwise the replace() calls would silently no-op and lose the value.
  const template = t(
    localizedTexts,
    "Tracker.Checkout.Period.EveryN",
    "every {count} {unit}"
  );
  const pattern =
    template.includes("{count}") && template.includes("{unit}")
      ? template
      : "every {count} {unit}";
  return pattern
    .replace("{count}", String(frequency))
    .replace("{unit}", unitLabel);
}
